import type { OntologyJsonObjectDto, OntologySensitivityDto, OntologySourceArtifactRefDto } from './ontology';

export const TALENT_EVIDENCE_GRAPH_SCHEMA_VERSION = 'talent_evidence_graph_v1' as const;
export const TALENT_EVIDENCE_EXTRACTION_SCHEMA_VERSION = 'talent_evidence_extraction_v1' as const;
export const TALENT_EVIDENCE_DECISION_SUPPORT_PROMOTION_TOOL =
  'talent_evidence_graph.evidence_review.promote' as const;

export type TalentEvidenceGraphSchemaVersion = typeof TALENT_EVIDENCE_GRAPH_SCHEMA_VERSION;
export type TalentEvidenceExtractionSchemaVersion = typeof TALENT_EVIDENCE_EXTRACTION_SCHEMA_VERSION;

export type TalentEvidenceEpistemicLabel =
  | 'Observed'
  | 'Planned'
  | 'Inferred'
  | 'Assumption'
  | 'Decision';

export type TalentEvidenceSignalKind =
  | 'capability_proficiency'
  | 'skill'
  | 'role_requirement'
  | 'performance_indicator'
  | 'potential_indicator'
  | 'collaboration_indicator'
  | 'leadership_indicator'
  | 'mobility'
  | 'flight_risk';

export type TalentEvidenceConsequentialUse =
  | 'allowed_for_decision_support'
  | 'blocked_pending_human_review';

export type TalentEvidenceActorContextDto = {
  actorUserId?: string | null;
  tenantId: string;
  permissions: string[];
  allowedOrgStateIds?: string[] | null;
  allowedPersonIds?: string[] | null;
  allowedPositionIds?: string[] | null;
  allowedInitiativeIds?: string[] | null;
};

export type TalentEvidenceGraphNodeKind =
  | 'person'
  | 'position'
  | 'org_unit'
  | 'capability'
  | 'skill_evidence'
  | 'cost'
  | 'allocation'
  | 'initiative'
  | 'objective'
  | 'work_item'
  | 'policy_rule';

export type TalentEvidenceGraphEdgeKind =
  | 'reports_to'
  | 'member_of'
  | 'has_cost'
  | 'has_evidence_for'
  | 'requires'
  | 'allocated_to'
  | 'works_on'
  | 'advances'
  | 'constrains';

export type TalentEvidenceSourceArtifactDto = {
  artifactId: string;
  artifactType: string;
  sourceKind: string;
  sourceId: string;
  tenantId: string;
  orgStateId: string;
  personId?: string | null;
  positionId?: string | null;
  sensitivity: OntologySensitivityDto | string;
  redactionPolicy: string;
  provider?: string | null;
  model?: string | null;
  promptVersion?: string | null;
  schemaVersion: string;
  inputHash: string;
  asOf: string;
};

export type TalentEvidenceSourceArtifactWithPayloadDto = TalentEvidenceSourceArtifactDto & {
  outputJson: OntologyJsonObjectDto;
};

export type TalentEvidenceLineageDto = {
  layer: string;
  runId?: string | null;
  artifactId?: string | null;
  artifactType?: string | null;
  sourceKind?: string | null;
  sourceId?: string | null;
  provider?: string | null;
  model?: string | null;
  promptVersion?: string | null;
  schemaVersion?: string | null;
  inputHash?: string | null;
  edgeId?: string | null;
};

export type TalentEvidenceExtractedSignalInputDto = {
  signalId: string;
  signalKind: TalentEvidenceSignalKind;
  personId?: string | null;
  positionId?: string | null;
  capabilityId?: string | null;
  capabilityName?: string | null;
  proficiency?: number | null;
  performance?: number | null;
  potential?: number | null;
  flightRisk?: string | null;
  confidence: number;
  sourceArtifactId: string;
  evidenceSummary?: string | null;
  label?: TalentEvidenceEpistemicLabel | string | null;
};

export type TalentEvidenceInitiativeRequirementInputDto = {
  initiativeId: string;
  initiativeName?: string | null;
  capabilityId: string;
  capabilityName?: string | null;
  requiredProficiency: number;
  weight: number;
  source: string;
  label: TalentEvidenceEpistemicLabel;
  confidence: number;
};

export type TalentEvidenceNormalizedSignalDto = TalentEvidenceExtractedSignalInputDto & {
  label: 'Inferred';
  source: string;
  asOf: string;
  tenantId: string;
  schemaVersion: TalentEvidenceGraphSchemaVersion;
  runId: string;
  lowConfidence: boolean;
  stale: boolean;
  biasProxyFlags: string[];
  consequentialUse: TalentEvidenceConsequentialUse;
  lineage: TalentEvidenceLineageDto[];
};

export type TalentEvidenceGraphNodeDto = {
  id: string;
  kind: TalentEvidenceGraphNodeKind;
  title: string;
  label: TalentEvidenceEpistemicLabel;
  source: string;
  asOf: string;
  tenantId: string;
  schemaVersion: TalentEvidenceGraphSchemaVersion;
  runId: string;
  confidence: number;
  lineage: TalentEvidenceLineageDto[];
  sourceArtifactRefs: OntologySourceArtifactRefDto[];
  properties: Record<string, unknown>;
};

export type TalentEvidenceGraphEdgeDto = {
  id: string;
  kind: TalentEvidenceGraphEdgeKind;
  fromNodeId: string;
  toNodeId: string;
  label: TalentEvidenceEpistemicLabel;
  source: string;
  asOf: string;
  tenantId: string;
  schemaVersion: TalentEvidenceGraphSchemaVersion;
  runId: string;
  confidence: number;
  lineage: TalentEvidenceLineageDto[];
  sourceArtifactRefs: OntologySourceArtifactRefDto[];
  properties: Record<string, unknown>;
};

export type TalentEvidenceGraphDto = {
  tenantId: string;
  orgStateId: string;
  runId: string;
  schemaVersion: TalentEvidenceGraphSchemaVersion;
  asOf: string;
  nodes: TalentEvidenceGraphNodeDto[];
  edges: TalentEvidenceGraphEdgeDto[];
  signals: TalentEvidenceNormalizedSignalDto[];
};

export type TalentEvidenceNormalizeExtractionInputDto = {
  tenantId: string;
  orgStateId: string;
  runId: string;
  extractedAt: string;
  authz: TalentEvidenceActorContextDto;
  maxEvidenceAgeDays?: number;
  sourceArtifacts: TalentEvidenceSourceArtifactDto[];
  signals: TalentEvidenceExtractedSignalInputDto[];
  initiativeRequirements?: TalentEvidenceInitiativeRequirementInputDto[];
};

export type TalentEvidenceExtractionInputDto = {
  tenantId: string;
  orgStateId: string;
  runId: string;
  extractedAt: string;
  authz: TalentEvidenceActorContextDto;
  provider?: 'openai' | 'ollama' | null;
  model?: string | null;
  tenantAiPolicy?: {
    openaiEnabled?: boolean | null;
    allowPiiToCloud?: boolean | null;
    allowPiiToLocal?: boolean | null;
    allowHighlySensitiveLocal?: boolean | null;
  } | null;
  sourceArtifacts: TalentEvidenceSourceArtifactWithPayloadDto[];
};

export type TalentEvidenceExtractionResultDto = {
  schemaVersion: TalentEvidenceExtractionSchemaVersion;
  tenantId: string;
  orgStateId: string;
  runId: string;
  extractedAt: string;
  sourceArtifacts: TalentEvidenceSourceArtifactDto[];
  signals: TalentEvidenceNormalizedSignalDto[];
  inputsHash: string;
};

export type TalentEvidencePersonFitStatus = 'fit' | 'partial' | 'blocked';

export type TalentEvidenceMetricDefinitionDto = {
  metricId: string;
  definition: string;
  unit: string;
  scope: string;
  timeframe: string;
  source: string;
  asOf: string;
  reconciliationRule: string;
  usageConstraint: string;
  label: TalentEvidenceEpistemicLabel;
};

export type TalentEvidencePersonFitDto = {
  personId: string;
  initiativeId: string;
  status: TalentEvidencePersonFitStatus;
  score: number;
  confidence: number;
  label: 'Inferred';
  blockedReasons: string[];
  dataGaps: string[];
  lineage: TalentEvidenceLineageDto[];
  capabilityProficiencies: Array<{
    capabilityId: string;
    requiredProficiency: number;
    proficiency: number | null;
    confidence: number | null;
    status: TalentEvidencePersonFitStatus;
  }>;
};

export type TalentEvidenceProjectionDto = {
  schemaVersion: TalentEvidenceGraphSchemaVersion;
  tenantId: string;
  orgStateId: string;
  initiativeId: string;
  asOf: string;
  personFits: TalentEvidencePersonFitDto[];
  blockedReasons: string[];
  metricDefinitions: TalentEvidenceMetricDefinitionDto[];
};

export type TalentEvidenceProjectionInputDto = {
  graph: TalentEvidenceGraphDto;
  initiativeId: string;
  personIds: string[];
  asOf: string;
  authz: TalentEvidenceActorContextDto;
};

export type TalentEvidenceDecisionSupportApprovalBindingDto = {
  toolName: string;
  activationApprovalRef: string;
  decisionType: string;
  scopeKind: string;
  scopeId: string;
};

export type TalentEvidencePromoteEvidenceInputDto = {
  graph: TalentEvidenceGraphDto;
  edgeIds: string[];
  approvalId: string;
  approvedAt: string;
  approvedToolCallIds: string[];
  activationApprovalBindings: TalentEvidenceDecisionSupportApprovalBindingDto[];
  approverUserIds: string[];
  authz: TalentEvidenceActorContextDto;
};
